import { ReactElement, cloneElement, Children } from 'react';
import { useFilterText } from './hooks';
import { SelectValueType, ChildrenWithInputText } from './types';
import { RawSelect, RawSelectProps } from './RawSelect';

/**
 *
 * In some cases with children the input text can be deeply nested.
 * This will cause the search to either not work or error out because cloneElement will mutate the prop incorrectly.
 * With this utils, if children exists in props it will traverse through all children and add the prop changes to the InputText component
 */

export const addInputPropsToChildren = (
  filterText: string,
  cb: (searchQuery: string) => void,
  children: ChildrenWithInputText | ChildrenWithInputText[],
): ReactElement[] =>
  Children.map<ReactElement, ReactElement>(children, (child) => {
    if (child.props?.type === 'search') {
      return cloneElement(child, {
        ...child.props,
        onChange: cb,
        value: filterText,
      });
    } else if (child.props?.children) {
      const formattedChildren = addInputPropsToChildren(filterText, cb, child.props.children);
      return cloneElement(child, {
        ...child.props,
        children: formattedChildren,
      });
    }
    return child;
  });

export function BasicSelectWithNestedChildren<TValue extends SelectValueType = string>({
  onSearchChange,
  children,
  ...props
}: RawSelectProps<TValue>) {
  const [filterText, setFilterText] = useFilterText('');
  const onChangeCb = (searchQuery: string) => {
    onSearchChange?.(searchQuery);
    setFilterText(searchQuery);
  };

  return (
    <RawSelect filterText={filterText} {...props}>
      {children && addInputPropsToChildren(filterText, onChangeCb, children)}
    </RawSelect>
  );
}
